import React, { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { RichTextEditor } from "../ui/RichTextEditor";
import { Modal } from "../ui/Modal";
import { useModalLanguage } from "../../context/ModalLanguageContext";
import { getThumbnailUrl } from "../../utils/imageUtils";
import { SupportedLanguage } from "../../types/translations.types";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import { Select } from "components/ui/Select";
interface LocationPricing {
  locationId: string;
  price?: number;
  inventoryQuantity?: number;
  materialCost?: number;
  isAvailable: boolean;
}

// Country Pricing Modal Component
interface LocationPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  locations: any[];
  locationPricing: LocationPricing[];
  basePrice: number;
  baseQuantity?: number;
  baseMaterialCost?: number;
  onUpdate: (locationPricing: LocationPricing[]) => void;
  isDigital?: boolean; // Whether the product is digital
}

const LocationPricingModal: React.FC<LocationPricingModalProps> = ({
  isOpen,
  onClose,
  title,
  locations,
  locationPricing,
  basePrice,
  baseQuantity,
  baseMaterialCost,
  onUpdate,
  isDigital = false,
}) => {
  const { t } = useTranslation();
  const { isPluginEnabled } = useStore();
  const isProfitPluginEnabled = isPluginEnabled("profit-rules");
  const updateLocationPricing = (locationId: string, updates: Partial<LocationPricing>) => {
    const updated = [...locationPricing];
    const existingIndex = updated.findIndex((lp) => lp.locationId === locationId);

    if (existingIndex >= 0) {
      updated[existingIndex] = { ...updated[existingIndex], ...updates };
    } else {
      updated.push({
        locationId,
        isAvailable: true,
        ...updates,
      });
    }
    onUpdate(updated);
  };

  const removeLocationPricing = (locationId: string) => {
    onUpdate(locationPricing.filter((lp) => lp.locationId !== locationId));
  };

  // Check for inconsistent inventory configuration
  const hasInconsistentInventory = useMemo(() => {
    const locationsWithInventory = locationPricing.filter(
      (lp) => lp.inventoryQuantity !== undefined && lp.inventoryQuantity !== null
    );
    // If some countries have inventory set but not all, it's inconsistent
    return locationsWithInventory.length > 0 && locationsWithInventory.length < locations.length;
  }, [locationPricing, locations]);

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleContentMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="xl">
      <div 
        className="space-y-4"
        onClick={handleContentClick}
        onMouseDown={handleContentMouseDown}
        onMouseUp={handleContentClick}
      >
        {hasInconsistentInventory && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
            <svg
              className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800">
                Inconsistent Inventory Configuration
              </p>
              <p className="text-xs text-yellow-700 mt-1">
                Some countries have inventory set while others don't. Countries without inventory will share the base quantity. Consider setting inventory for all countries or removing it from all countries for consistency.
              </p>
            </div>
          </div>
        )}
        <p className="text-sm text-gray-600">
          Configure pricing and inventory per country. Leave empty to use base values.
        </p>
        <div 
          className="border border-gray-200 rounded-lg overflow-hidden" 
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <div 
            className="overflow-x-auto" 
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <table 
              className="min-w-full divide-y divide-gray-200" 
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
            >
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-48">
                    {t("admin.productForm.country")}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                    Available
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Price
                  </th>
                  {isProfitPluginEnabled && (
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Material Cost
                   </th>
                  )}
                  {!isDigital && (
                   <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Quantity
                   </th>
                  )}
                </tr>
              </thead>
              <tbody 
                className="bg-white divide-y divide-gray-200" 
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
              >
                {locations.map((location) => {
                  const lp = locationPricing.find((l) => l.locationId === location._id);
                  const isAvailable = lp?.isAvailable ?? true;
                  const locationPrice = lp?.price;
                  const locationMaterialCost = lp?.materialCost;
                  const locationQuantity = lp?.inventoryQuantity;

                  return (
                    <tr 
                      key={location._id} 
                      className="hover:bg-gray-50" 
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onMouseUp={(e) => e.stopPropagation()}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{location.country}</div>
                          <div className="text-xs text-gray-500">{location.countryCode} - {location.currency}</div>
                        </div>
                      </td>
                      <td 
                        className="px-4 py-3 whitespace-nowrap text-center"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isAvailable}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateLocationPricing(location._id, { isAvailable: e.target.checked });
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                          }}
                          onMouseUp={(e) => {
                            e.stopPropagation();
                            e.nativeEvent.stopImmediatePropagation();
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div 
                          className="flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={locationPrice !== undefined ? locationPrice : ""}
                            placeholder={basePrice?.toString() || "0"}
                            onChange={(e) => {
                              e.stopPropagation();
                              const value = e.target.value;
                              if (value === "") {
                                if (lp) {
                                  const { price, ...rest } = lp;
                                  updateLocationPricing(location._id, rest);
                                } else {
                                  removeLocationPricing(location._id);
                                }
                              } else {
                                const numValue = parseFloat(value);
                                if (!isNaN(numValue) && numValue >= 0) {
                                  updateLocationPricing(location._id, { price: numValue });
                                }
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="w-32 min-w-32"
                            label=""
                          />
                          <span className="text-sm text-gray-600 font-medium">{location.currencySymbol}</span>
                        </div>
                      </td>
                      {isProfitPluginEnabled && (
                       <td className="px-4 py-3 whitespace-nowrap">
                        <div 
                         onClick={(e) => e.stopPropagation()}
                         onMouseDown={(e) => e.stopPropagation()}
                        >
                         <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={locationMaterialCost !== undefined ? locationMaterialCost : ""}
                          placeholder={baseMaterialCost?.toString() || ""}
                          onChange={(e) => {
                           e.stopPropagation();
                           const value = e.target.value;
                           if (value === "") {
                            if (lp) {
                             const { materialCost, ...rest } = lp;
                             updateLocationPricing(location._id, rest);
                            } else {
                             removeLocationPricing(location._id);
                            }
                           } else {
                            const numValue = parseFloat(value);
                            if (!isNaN(numValue) && numValue >= 0) {
                             updateLocationPricing(location._id, { materialCost: numValue });
                            }
                           }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-32 min-w-32"
                          label=""
                         />
                        </div>
                       </td>
                      )}
                      {!isDigital && (
                       <td className="px-4 py-3 whitespace-nowrap">
                        <div 
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={locationQuantity !== undefined ? locationQuantity : ""}
                            placeholder={baseQuantity?.toString() || ""}
                            onChange={(e) => {
                              e.stopPropagation();
                              const value = e.target.value;
                              if (value === "") {
                                if (lp) {
                                  const { inventoryQuantity, ...rest } = lp;
                                  updateLocationPricing(location._id, rest);
                                } else {
                                  removeLocationPricing(location._id);
                                }
                              } else {
                                const numValue = parseInt(value, 10);
                                if (!isNaN(numValue) && numValue >= 0) {
                                  updateLocationPricing(location._id, { inventoryQuantity: numValue });
                                }
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="w-32 min-w-32"
                            label=""
                          />
                        </div>
                       </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export interface VariantCombination {
 combination: string; // Format: "Size:Small|Color:Red"
 price: number;
 inventoryQuantity?: number;
 description?: string;
 imageIndex?: number;
 locationPricing?: LocationPricing[];
 digitalFiles?: Array<{
  key: string;
  filename: string;
  size?: number;
  contentType?: string;
  countryCodes?: string[];
 }>;
 translations?: { [languageCode: string]: string };
 // Shipping-related fields
 weight?: number;
 weightUnit?: "kg" | "g" | "lb" | "oz";
 dimensions?: {
  length: number;
  width: number;
  height: number;
  unit: "cm" | "in";
 };
 fragility?: "none" | "low" | "medium" | "high";
 requiresSpecialHandling?: boolean;
}

interface VariantOption {
 name: string;
 values: Array<{ value: string; isActive: boolean }>;
 required: boolean;
}

interface VariantCombinationsTableProps {
 variantOptions: VariantOption[];
 images: Array<{ url: string; alt?: string; isPrimary?: boolean; translations?: { [languageCode: string]: string } }>;
 basePrice: number;
 baseMaterialCost?: number;
 variantCombinations: VariantCombination[];
 onChange: (combinations: VariantCombination[]) => void;
 variantCombinationMaterialCosts: Record<string, number | undefined>;
 setVariantCombinationMaterialCosts: React.Dispatch<React.SetStateAction<Record<string, number | undefined>>>;
 isDigital?: boolean; // Whether the product is digital
}

// Image Selector Component with Thumbnails
interface ImageSelectorProps {
 images: Array<{ url: string; alt?: string; isPrimary?: boolean; translations?: { [languageCode: string]: string } }>;
 value: number | undefined;
 onChange: (imageIndex: number | undefined) => void;
 currentLanguage: SupportedLanguage;
 t: (key: string) => string;
}

const ImageSelector: React.FC<ImageSelectorProps> = ({
 images,
 value,
 onChange,
 currentLanguage,
 t,
}) => {
 const [isOpen, setIsOpen] = useState(false);
 const dropdownRef = useRef<HTMLDivElement>(null);

 useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
   if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
    setIsOpen(false);
   }
  };

  if (isOpen) {
   document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
   document.removeEventListener("mousedown", handleClickOutside);
  };
 }, [isOpen]);

 const getImageUrl = (image: { url: string; translations?: { [languageCode: string]: string } }): string => {
  if (currentLanguage === "en" || !image.translations) {
   return image.url;
  }
  // Handle both Map and plain object translations
  if (image.translations instanceof Map) {
   return image.translations.get(currentLanguage) || image.url;
  }
  return image.translations[currentLanguage] || image.url;
 };

 const selectedImage = value !== undefined && value >= 0 && value < images.length ? images[value] : null;

 return (
  <div className="relative" ref={dropdownRef}>
   <button
    type="button"
    onClick={() => setIsOpen(!isOpen)}
    className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-left flex items-center gap-2 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
   >
    {selectedImage ? (
     <>
      <img
       src={getThumbnailUrl(getImageUrl(selectedImage))}
       alt={selectedImage.alt || `Image ${value! + 1}`}
       className="w-8 h-8 object-cover rounded border border-gray-200"
       onError={(e) => {
        const target = e.target as HTMLImageElement;
        target.src = getThumbnailUrl(selectedImage.url);
       }}
      />
      <span className="flex-1 truncate">
       {t("admin.productForm.image")} {value! + 1}
       {selectedImage.isPrimary && ` (${t("admin.productForm.primary")})`}
      </span>
     </>
    ) : (
     <span className="flex-1 text-gray-500">{t("admin.productForm.noImage") || "No Image"}</span>
    )}
    <svg
     className={`w-4 h-4 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
     fill="none"
     stroke="currentColor"
     viewBox="0 0 24 24"
    >
     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
   </button>

   {isOpen && (
    <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
     <button
      type="button"
      onClick={() => {
       onChange(undefined);
       setIsOpen(false);
      }}
      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
       value === undefined ? "bg-blue-50 text-blue-700" : ""
      }`}
     >
      <div className="w-8 h-8 border border-gray-300 rounded flex items-center justify-center">
       <span className="text-gray-400 text-xs">—</span>
      </div>
      <span>{t("admin.productForm.noImage") || "No Image"}</span>
     </button>
      {images.map((img, idx) => {
       const imageUrl = getImageUrl(img);
       return (
        <button
         key={idx}
         type="button"
         onClick={() => {
          onChange(idx);
          setIsOpen(false);
         }}
         className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
          value === idx ? "bg-blue-50 text-blue-700" : ""
         }`}
        >
         <img
          src={getThumbnailUrl(imageUrl)}
          alt={img.alt || `Image ${idx + 1}`}
          className="w-8 h-8 object-cover rounded border border-gray-200 flex-shrink-0"
          onError={(e) => {
           const target = e.target as HTMLImageElement;
           target.src = getThumbnailUrl(img.url);
          }}
         />
         <span className="flex-1 truncate">
          {t("admin.productForm.image")} {idx + 1}
          {img.isPrimary && ` (${t("admin.productForm.primary")})`}
         </span>
        </button>
       );
      })}
    </div>
   )}
  </div>
 );
};

export const VariantCombinationsTable: React.FC<VariantCombinationsTableProps> = ({
 variantOptions,
 images,
 basePrice,
 baseMaterialCost,
 variantCombinations = [],
 onChange,
 variantCombinationMaterialCosts,
 setVariantCombinationMaterialCosts,
 isDigital = false,
}) => {
 const { t } = useTranslation();
 const { api } = useApi();
 const { isPluginEnabled } = useStore();
 const isProfitPluginEnabled = isPluginEnabled("profit-rules");
 const { currentLanguage } = useModalLanguage();
 const [editingDescriptionIndex, setEditingDescriptionIndex] = useState<number | null>(null);
 const [editingDescription, setEditingDescription] = useState<string>("");
 const [locations, setLocations] = useState<any[]>([]);
 const [locationModalOpen, setLocationModalOpen] = useState<string | null>(null);

 // Generate all possible combinations from variant options
 const allCombinations = useMemo(() => {
  if (variantOptions.length === 0) return [];

  const generateCombos = (
   currentCombo: Array<{ optionName: string; value: string }>,
   optionIndex: number
  ): Array<Array<{ optionName: string; value: string }>> => {
   if (optionIndex >= variantOptions.length) {
    return currentCombo.length > 0 ? [currentCombo] : [];
   }

   const option = variantOptions[optionIndex];
   const activeValues = option.values.filter((v) => v.isActive);

   if (activeValues.length === 0) {
    return generateCombos(currentCombo, optionIndex + 1);
   }

   const combos: Array<Array<{ optionName: string; value: string }>> = [];
   for (const value of activeValues) {
    const newCombo = [...currentCombo, { optionName: option.name, value: value.value }];
    combos.push(...generateCombos(newCombo, optionIndex + 1));
   }

   return combos;
  };

  const combos = generateCombos([], 0);
  return combos.map((combo) => {
   const comboString = combo
    .map(({ optionName, value }) => `${optionName}:${value}`)
    .sort()
    .join("|");
   return { combo, comboString };
  });
 }, [variantOptions]);

 // Merge existing combinations with all possible combinations
 const tableRows = useMemo(() => {
  const existingMap = new Map<string, VariantCombination>();
  variantCombinations.forEach((combo) => {
   existingMap.set(combo.combination, combo);
  });

  return allCombinations.map(({ comboString, combo }) => {
   const existing = existingMap.get(comboString);
   return {
    combination: comboString,
    combo,
    price: existing?.price ?? basePrice,
    inventoryQuantity: existing?.inventoryQuantity,
    description: existing?.description,
    imageIndex: existing?.imageIndex,
    translations: existing?.translations || {},
    weight: existing?.weight,
    weightUnit: existing?.weightUnit || "kg",
    dimensions: existing?.dimensions,
    fragility: existing?.fragility || "none",
    requiresSpecialHandling: existing?.requiresSpecialHandling || false,
   };
  });
 }, [allCombinations, variantCombinations, basePrice]);

 const handlePriceChange = (combination: string, price: number) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].price = price;
  } else {
   updated.push({
    combination,
    price,
   });
  }
  onChange(updated);
 };

 const handleQuantityChange = (combination: string, quantity: number | undefined) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].inventoryQuantity = quantity;
  } else {
   updated.push({
    combination,
    price: basePrice,
    inventoryQuantity: quantity,
   });
  }
  onChange(updated);
 };

 const handleImageChange = (combination: string, imageIndex: number | undefined) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].imageIndex = imageIndex;
  } else {
   updated.push({
    combination,
    price: basePrice,
    imageIndex,
   });
  }
  onChange(updated);
 };

 const handleDescriptionChange = (combination: string, description: string) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   if (currentLanguage === "en") {
    updated[index].description = description;
   } else {
    updated[index].translations = {
     ...updated[index].translations,
     [currentLanguage]: description,
    };
   }
  } else {
   const newCombo: VariantCombination = {
    combination,
    price: basePrice,
   };
   if (currentLanguage === "en") {
    newCombo.description = description;
   } else {
    newCombo.translations = { [currentLanguage]: description };
   }
   updated.push(newCombo);
  }
  onChange(updated);
 };

 const handleWeightChange = (combination: string, weight: number | undefined) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].weight = weight;
  } else {
   updated.push({
    combination,
    price: basePrice,
    weight,
    weightUnit: "kg",
   });
  }
  onChange(updated);
 };

 const handleWeightUnitChange = (combination: string, weightUnit: "kg" | "g" | "lb" | "oz") => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].weightUnit = weightUnit;
  } else {
   updated.push({
    combination,
    price: basePrice,
    weightUnit,
   });
  }
  onChange(updated);
 };

 const handleDimensionsChange = (combination: string, dimensions: { length: number; width: number; height: number; unit: "cm" | "in" } | undefined) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].dimensions = dimensions;
  } else {
   updated.push({
    combination,
    price: basePrice,
    dimensions,
   });
  }
  onChange(updated);
 };

 const handleFragilityChange = (combination: string, fragility: "none" | "low" | "medium" | "high") => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].fragility = fragility;
  } else {
   updated.push({
    combination,
    price: basePrice,
    fragility,
   });
  }
  onChange(updated);
 };

 const handleSpecialHandlingChange = (combination: string, requiresSpecialHandling: boolean) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].requiresSpecialHandling = requiresSpecialHandling;
  } else {
   updated.push({
    combination,
    price: basePrice,
    requiresSpecialHandling,
   });
  }
  onChange(updated);
 };

 const openDescriptionEditor = (row: typeof tableRows[0]) => {
  const description =
   currentLanguage === "en"
    ? row.description || ""
    : row.translations?.[currentLanguage] || "";
  setEditingDescription(description);
  setEditingDescriptionIndex(tableRows.indexOf(row));
 };

 const saveDescription = () => {
  if (editingDescriptionIndex !== null) {
   const row = tableRows[editingDescriptionIndex];
   handleDescriptionChange(row.combination, editingDescription);
   setEditingDescriptionIndex(null);
   setEditingDescription("");
  }
 };

 const getDescriptionPreview = (row: typeof tableRows[0]): string => {
  const desc =
   currentLanguage === "en"
    ? row.description
    : row.translations?.[currentLanguage];
  if (!desc) return "";
  // Strip HTML tags for preview
  return desc.replace(/<[^>]*>/g, "").substring(0, 50) + (desc.length > 50 ? "..." : "");
 };

 const getCombinationDisplayText = (row: typeof tableRows[0]): string => {
  return row.combo.map(({ optionName, value }) => `${optionName}: ${value}`).join(", ");
 };

 const getModalTitle = (): string => {
  if (editingDescriptionIndex === null) {
   return t("admin.productForm.editDescription") || "Edit Description";
  }
  const row = tableRows[editingDescriptionIndex];
  const combinationText = getCombinationDisplayText(row);
  const languageNames: Record<SupportedLanguage, string> = {
   en: "English",
   de: "German",
   ar: "Arabic",
  };
  const languageText = languageNames[currentLanguage] || currentLanguage.toUpperCase();
  return `${t("admin.productForm.editDescription") || "Edit Description"} - ${combinationText} (${languageText})`;
 };

 // Fetch locations when component mounts
 useEffect(() => {
  const fetchLocations = async () => {
   try {
    const response = await api.get("/locations/admin?isActive=true");
    setLocations(response.data.locations || []);
   } catch (err) {
    console.error("Failed to fetch locations:", err);
   }
  };
  fetchLocations();
 }, [api]);

 const handleLocationPricingUpdate = (combination: string, locationPricing: LocationPricing[]) => {
  const updated = [...variantCombinations];
  const index = updated.findIndex((c) => c.combination === combination);
  if (index >= 0) {
   updated[index].locationPricing = locationPricing;
  } else {
   updated.push({
    combination,
    price: basePrice,
    locationPricing,
   });
  }
  onChange(updated);
 };

 const getLocationModalTitle = (combination: string): string => {
  const row = tableRows.find((r) => r.combination === combination);
  if (row) {
   return `${t("admin.productForm.countryManagement")} - ${getCombinationDisplayText(row)}`;
  }
  return t("admin.productForm.countryManagement");
 };

 if (variantOptions.length === 0) {
  return null;
 }

 return (
  <div className="space-y-4">
   <div>
    <h4 className="text-md font-semibold mb-2">
     {t("admin.productForm.variantCombinations") || "Variant Combinations"}
    </h4>
    <p className="text-sm text-gray-600 mb-4">
     {t("admin.productForm.variantCombinationsDescription") ||
      "Manage prices, inventory, images, and descriptions for each variant combination."}
    </p>
   </div>

   <div className="overflow-x-auto border border-gray-200 rounded-lg">
    <table className="min-w-full divide-y divide-gray-200">
     <thead className="bg-gray-50">
      <tr>
       <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
        {t("admin.productForm.combination") || "Combination"}
       </th>
       <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-40">
        {t("admin.productForm.price") || "Price"}
       </th>
       {isProfitPluginEnabled && (
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-40">
         {t("admin.productProfitSettings.materialCosts.materialCost") || "Material Cost"}
        </th>
       )}
       {!isDigital && (
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
         {t("admin.productForm.quantity") || "Quantity"}
        </th>
       )}
       <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-40">
        {t("admin.productForm.image") || "Image"}
       </th>
       <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
        {t("admin.productForm.description") || "Description"}
       </th>
       {!isDigital && (
        <>
         <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
          {t("admin.productForm.shipping") || "Shipping"}
         </th>
         <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
          {t("admin.productForm.dimensions") || "Dimensions"}
         </th>
         <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
          {t("admin.productForm.fragility") || "Fragility"}
         </th>
        </>
       )}
       {currentLanguage === "en" && (
        <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
         {t("admin.productForm.countries")}
        </th>
       )}
      </tr>
     </thead>
     <tbody className="bg-white divide-y divide-gray-200">
      {tableRows.map((row, _index) => {
       const existingCombo = variantCombinations.find((c) => c.combination === row.combination);
       const locationPricing = existingCombo?.locationPricing || [];
       
       // Check if all countries have all fields set (price, inventoryQuantity, isAvailable)
       const hasAllFieldsSet = (() => {
         if (locations.length === 0 || locationPricing.length === 0) return false;
         // Must have location pricing for all countries
         if (locationPricing.length < locations.length) return false;
         // Check that all location pricing entries have all fields set
         return locationPricing.every((lp) => {
           return (
             lp.price !== undefined &&
             lp.price !== null &&
             lp.inventoryQuantity !== undefined &&
             lp.inventoryQuantity !== null &&
             lp.isAvailable !== undefined
           );
         });
       })();
       
       // Check if some (but not all) fields are set
       const hasPartialFieldsSet = (() => {
         if (locations.length === 0) return false;
         if (locationPricing.length === 0) return false;
         // If we have location pricing but not all fields are set, it's partial
         return !hasAllFieldsSet && locationPricing.some((lp) => {
           return (
             lp.price !== undefined ||
             lp.inventoryQuantity !== undefined ||
             lp.isAvailable !== undefined
           );
         });
       })();
       
       // Check for inconsistent inventory configuration
       const hasInconsistentInventory = (() => {
         if (locations.length === 0) return false;
         const locationsWithInventory = locationPricing.filter(
           (lp) => lp.inventoryQuantity !== undefined && lp.inventoryQuantity !== null
         );
         // If some countries have inventory set but not all, it's inconsistent
         return locationsWithInventory.length > 0 && locationsWithInventory.length < locations.length;
       })();
       
       return (
        <tr key={row.combination} className="hover:bg-gray-50">
         <td className="px-4 py-3">
          <div className="flex flex-wrap gap-1">
           {row.combo.map(({ optionName, value }) => (
            <span
             key={`${optionName}-${value}`}
             className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded"
            >
             {optionName}: {value}
            </span>
           ))}
          </div>
         </td>
         <td className="px-4 py-3">
          <Input
           type="number"
           step="0.01"
           value={row.price}
           onChange={(e) => handlePriceChange(row.combination, parseFloat(e.target.value) || 0)}
           className="w-full min-w-24"
          />
         </td>
         {isProfitPluginEnabled && (
          <td className="px-4 py-3">
           <Input
            type="number"
            step="0.01"
            min="0"
            value={variantCombinationMaterialCosts[row.combination] ?? ""}
            onChange={(e) => {
             const value = e.target.value;
             setVariantCombinationMaterialCosts((prev) => ({
              ...prev,
              [row.combination]: value ? parseFloat(value) : undefined,
             }));
            }}
            placeholder={baseMaterialCost?.toString() || ""}
            className="w-full min-w-24"
           />
          </td>
         )}
         {!isDigital && (
          <td className="px-4 py-3">
           <Input
            type="number"
            step="1"
            min="0"
            value={row.inventoryQuantity ?? ""}
            onChange={(e) =>
             handleQuantityChange(
              row.combination,
              e.target.value ? parseInt(e.target.value, 10) : undefined
             )
            }
            className="w-full min-w-24"
           />
          </td>
         )}
         <td className="px-4 py-3">
          <ImageSelector
           images={images}
           value={row.imageIndex}
           onChange={(imageIndex) => handleImageChange(row.combination, imageIndex)}
           currentLanguage={currentLanguage}
           t={t}
          />
         </td>
         <td className="px-4 py-3">
          {getDescriptionPreview(row) && (
           <div className="text-sm text-gray-600 mb-1">{getDescriptionPreview(row)}</div>
          )}
          <Button
           type="button"
           variant="outline"
           size="sm"
           onClick={() => openDescriptionEditor(row)}
          >
           {row.description || row.translations?.[currentLanguage]
            ? t("admin.productForm.editDescription") || "Edit"
            : t("admin.productForm.addDescription") || "Add"}
          </Button>
         </td>
         {!isDigital && (
          <>
           <td className="px-4 py-3">
            <div className="space-y-2">
             <div className="flex gap-2 items-center">
              <Input
               type="number"
               step="0.01"
               min="0"
               value={row.weight ?? ""}
               onChange={(e) =>
                handleWeightChange(
                 row.combination,
                 e.target.value ? parseFloat(e.target.value) : undefined
                )
               }
               placeholder="0"
               className="w-20 min-w-20"
              />
              <Select
               value={row.weightUnit || "kg"}
               onChange={(e) =>
                handleWeightUnitChange(
                 row.combination,
                 e.target.value as "kg" | "g" | "lb" | "oz"
                )
               }
               className="w-20 min-w-20"
               options={[
                 { value: "kg", label: "kg" },
                 { value: "g", label: "g" },
                 { value: "lb", label: "lb" },
                 { value: "oz", label: "oz" },
               ]}
              />
             </div>
             <label className="flex items-center gap-2 text-xs">
              <input
               type="checkbox"
               checked={row.requiresSpecialHandling || false}
               onChange={(e) =>
                handleSpecialHandlingChange(row.combination, e.target.checked)
               }
               className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-600">{t("admin.productForm.specialHandling") || "Special Handling"}</span>
             </label>
            </div>
           </td>
           <td className="px-4 py-3">
            <div className="space-y-1">
             <div className="flex gap-1 items-center">
              <Input
               type="number"
               step="0.01"
               min="0"
               value={row.dimensions?.length ?? ""}
               onChange={(e) => {
                const length = e.target.value ? parseFloat(e.target.value) : undefined;
                handleDimensionsChange(row.combination, length ? {
                 ...(row.dimensions || { width: 0, height: 0, unit: "cm" }),
                 length,
                } : undefined);
               }}
               placeholder="L"
               className="w-16 min-w-16"
              />
              <span className="text-gray-400">×</span>
              <Input
               type="number"
               step="0.01"
               min="0"
               value={row.dimensions?.width ?? ""}
               onChange={(e) => {
                const width = e.target.value ? parseFloat(e.target.value) : undefined;
                handleDimensionsChange(row.combination, width ? {
                 ...(row.dimensions || { length: 0, height: 0, unit: "cm" }),
                 width,
                } : undefined);
               }}
               placeholder="W"
               className="w-16 min-w-16"
              />
              <span className="text-gray-400">×</span>
              <Input
               type="number"
               step="0.01"
               min="0"
               value={row.dimensions?.height ?? ""}
               onChange={(e) => {
                const height = e.target.value ? parseFloat(e.target.value) : undefined;
                handleDimensionsChange(row.combination, height ? {
                 ...(row.dimensions || { length: 0, width: 0, unit: "cm" }),
                 height,
                } : undefined);
               }}
               placeholder="H"
               className="w-16 min-w-16"
              />
              <Select
               value={row.dimensions?.unit || "cm"}
               onChange={(e) => {
                const unit = e.target.value as "cm" | "in";
                handleDimensionsChange(row.combination, row.dimensions ? {
                 ...row.dimensions,
                 unit,
                } : { length: 0, width: 0, height: 0, unit });
               }}
               className="w-16 min-w-16"
               options={[
                 { value: "cm", label: "cm" },
                 { value: "in", label: "in" },
               ]}
              />
             </div>
            </div>
           </td>
           <td className="px-4 py-3">
            <Select
             value={row.fragility || "none"}
             onChange={(e) =>
              handleFragilityChange(
               row.combination,
               e.target.value as "none" | "low" | "medium" | "high"
              )
             }
             className="w-full min-w-24"
             options={[
               { value: "none", label: t("admin.productForm.fragilityNone") || "None" },
               { value: "low", label: t("admin.productForm.fragilityLow") || "Low" },
               { value: "medium", label: t("admin.productForm.fragilityMedium") || "Medium" },
               { value: "high", label: t("admin.productForm.fragilityHigh") || "High" },
             ]}
            />
           </td>
          </>
         )}
         {currentLanguage === "en" && (
          <td className="px-4 py-3">
           <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setLocationModalOpen(row.combination)}
            className={`relative ${
              hasInconsistentInventory || hasPartialFieldsSet
                ? "border-yellow-500 text-yellow-700 hover:border-yellow-600"
                : hasAllFieldsSet
                ? "border-blue-500 text-blue-600"
                : ""
            }`}
            title={
              hasInconsistentInventory
                ? "Inconsistent inventory: Some countries have inventory set while others don't"
                : hasPartialFieldsSet
                ? "Partial configuration: Some country fields are set but not all"
                : hasAllFieldsSet
                ? "All country fields are configured"
                : undefined
            }
           >
            <span className="flex items-center gap-1.5">
              {hasInconsistentInventory || hasPartialFieldsSet ? (
                <svg
                  className="w-4 h-4 text-yellow-600 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : hasAllFieldsSet ? (
                <span className="text-blue-600">✓</span>
              ) : null}
              {t("admin.productForm.countries")}
            </span>
           </Button>
          </td>
         )}
        </tr>
       );
      })}
     </tbody>
    </table>
   </div>

   {/* Description Editor Modal */}
   <Modal
    isOpen={editingDescriptionIndex !== null}
    onClose={() => {
     setEditingDescriptionIndex(null);
     setEditingDescription("");
    }}
    title={getModalTitle()}
    size="lg"
    footer={
     <div className="flex gap-2 justify-end">
      <Button
       type="button"
       variant="outline"
       onClick={() => {
        setEditingDescriptionIndex(null);
        setEditingDescription("");
       }}
      >
       {t("common.cancel")}
      </Button>
      <Button type="button" onClick={saveDescription}>
       {t("common.save")}
      </Button>
     </div>
    }
   >
    <RichTextEditor
     value={editingDescription}
     onChange={setEditingDescription}
     height="400px"
     placeholder={t("admin.productForm.variantDescriptionPlaceholder") || "Enter description..."}
    />
   </Modal>

   {/* Country Pricing Modals */}
   {locationModalOpen && (
    <LocationPricingModal
     isOpen={!!locationModalOpen}
     onClose={() => setLocationModalOpen(null)}
     title={getLocationModalTitle(locationModalOpen)}
     locations={locations}
     locationPricing={
      variantCombinations.find((c) => c.combination === locationModalOpen)?.locationPricing || []
     }
     basePrice={
      variantCombinations.find((c) => c.combination === locationModalOpen)?.price || basePrice
     }
     baseQuantity={
      variantCombinations.find((c) => c.combination === locationModalOpen)?.inventoryQuantity
     }
     baseMaterialCost={
      variantCombinationMaterialCosts[locationModalOpen] || baseMaterialCost
     }
     isDigital={isDigital}
     onUpdate={(locationPricing: LocationPricing[]) => {
      handleLocationPricingUpdate(locationModalOpen, locationPricing);
    }}
    />
   )}
  </div>
 );
};

